import { ApprovalRule } from "@p0security/shared/types/policy/types";
import { hasApprovalOptions } from "@p0security/shared/types/policy/util";
import { App, Flex } from "@p0security/ui";
import { PolicyEditorContext } from "components/Jit/Policy/store/RoutingRuleEditorContext";
import { EditableList } from "components/common/EditableList";
import { isEqual } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { EditorBackButton } from "../EditorBackButton";
import {
  ApprovalOptionsPreview,
  MaxDurationTag,
} from "../previews/ApprovalOptionsPreview";
import { ApprovalPreview } from "../previews/ApprovalPreview";
import { AccessRuleEditor } from "./AccessRuleEditor";

type Props = {
  /* Set to true when an access rule is being edited */
  onAccessRuleEditInProgress: (editing: boolean) => void;
  /* When true, open the "add new rule" editor immediately */
  defaultOpen?: boolean;
};

export const AccessEditor: React.FC<Props> = ({
  onAccessRuleEditInProgress,
  defaultOpen = false,
}) => {
  const { approval, setApproval } = useContext(PolicyEditorContext);
  const { modal } = App.useApp();
  const [isAddingNewRule, setIsAddingNewRule] = useState(
    defaultOpen && approval.length === 0
  );
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);

  const [localRule, setLocalRule] = useState<ApprovalRule | undefined>(
    undefined
  );
  const isDirty = useMemo(() => {
    if (isAddingNewRule) return localRule !== undefined;
    if (editingRuleIndex !== null) {
      return !isEqual(approval[editingRuleIndex], localRule);
    }
    return false;
  }, [localRule, isAddingNewRule, editingRuleIndex, approval]);

  const backToAccessControl = useCallback(() => {
    setIsAddingNewRule(false);
    setEditingRuleIndex(null);
    setLocalRule(undefined);
    onAccessRuleEditInProgress(false);
  }, [onAccessRuleEditInProgress]);

  const handleStartAddNewAccessRule = useCallback(() => {
    setIsAddingNewRule(true);
    setEditingRuleIndex(null);
    setLocalRule(undefined);
    onAccessRuleEditInProgress(true);
  }, [onAccessRuleEditInProgress]);

  const handleStartEditAccessRule = useCallback(
    (index: number) => {
      setIsAddingNewRule(false);
      setEditingRuleIndex(index);
      setLocalRule(approval[index]);
      onAccessRuleEditInProgress(true);
    },
    [approval, onAccessRuleEditInProgress]
  );

  const handleEditAccessRule = useCallback(
    (rule: ApprovalRule) => {
      if (editingRuleIndex === null) {
        return;
      }

      const newApproval = [...approval];
      newApproval[editingRuleIndex] = rule;
      setApproval(newApproval);
      backToAccessControl();
    },
    [approval, backToAccessControl, editingRuleIndex, setApproval]
  );

  const handleAddNewAccessRule = useCallback(
    (rule: ApprovalRule) => {
      setApproval([...approval, rule]);
      backToAccessControl();
    },
    [approval, backToAccessControl, setApproval]
  );

  const handleDeleteAccessRule = useCallback(
    (index: number) => {
      modal.confirm({
        title: "Delete access rule?",
        content: "This action cannot be undone",
        okType: "danger",
        okText: "Yes, delete access rule.",
        onOk() {
          backToAccessControl();
          const newApproval = approval.filter((_, i) => i !== index);
          setApproval(newApproval);
        },
      });
    },
    [approval, modal, setApproval, backToAccessControl]
  );

  const renderApprovalRule = useCallback(
    (rule: ApprovalRule) => (
      // One gap for every action type, so tags sit the same distance away
      <Flex align="center" gap={8}>
        <ApprovalPreview approval={rule} />
        {hasApprovalOptions(rule) && (
          <ApprovalOptionsPreview approvalOptions={rule.options} />
        )}
        <MaxDurationTag approval={rule} />
      </Flex>
    ),
    []
  );

  const isEditorOpen = isAddingNewRule || editingRuleIndex !== null;
  // Hide the list entirely when the editor is open and there is nothing to
  // show — otherwise the empty state visually overlaps with the open editor.
  const showList = !isEditorOpen || approval.length > 0;

  const showCancelButton = !(isAddingNewRule && approval.length === 0);

  const cancelButton = useMemo(
    () =>
      showCancelButton ? (
        <EditorBackButton
          onBack={backToAccessControl}
          isDirty={isDirty}
          isAdding={isAddingNewRule}
          name="Access Rule"
        />
      ) : undefined,
    [backToAccessControl, isDirty, isAddingNewRule, showCancelButton]
  );

  const newRuleContextValue = useMemo(
    () => ({ localRule, setLocalRule, isDirty, isNew: true }),
    [localRule, setLocalRule, isDirty]
  );

  const editRuleContextValue = useMemo(
    () => ({ localRule, setLocalRule, isDirty, isNew: false }),
    [localRule, setLocalRule, isDirty]
  );

  return (
    <Flex vertical gap={16} className="w-full">
      {showList && (
        <EditableList
          items={approval}
          onEditItem={handleStartEditAccessRule}
          onDeleteItem={handleDeleteAccessRule}
          onAddItem={handleStartAddNewAccessRule}
          renderItem={renderApprovalRule}
          addButtonText="Add New Access Rule"
          editing={isEditorOpen}
          minItems={1}
        />
      )}
      {isAddingNewRule ? (
        <LocalAccessRuleContext.Provider value={newRuleContextValue}>
          <AccessRuleEditor
            cancelButton={cancelButton}
            onSave={handleAddNewAccessRule}
          />
        </LocalAccessRuleContext.Provider>
      ) : editingRuleIndex !== null ? (
        <LocalAccessRuleContext.Provider value={editRuleContextValue}>
          <AccessRuleEditor
            cancelButton={cancelButton}
            onSave={handleEditAccessRule}
          />
        </LocalAccessRuleContext.Provider>
      ) : null}
    </Flex>
  );
};

type LocalAccessRule = {
  localRule: ApprovalRule | undefined;
  setLocalRule: (rule: ApprovalRule | undefined) => void;
  isDirty: boolean;
  isNew: boolean;
};

export const LocalAccessRuleContext = createContext<LocalAccessRule>({
  localRule: undefined,
  setLocalRule: () => {},
  isDirty: false,
  isNew: false,
});
